import { useEffect, useRef, useState } from "react";
import { useMap } from "react-leaflet";
import { useSelector } from 'react-redux';
import L from "leaflet";
import "leaflet-routing-machine";
import "leaflet-routing-machine/dist/leaflet-routing-machine.css";
import "leaflet-control-geocoder";
import { useTranslation } from 'react-i18next';
import config from '../../../config';
import { createRotatedRouteIcon } from "../markers/RouteIcons";
import { selectCurrentBearing, selectIsHeadingUp } from "../mapSlice";

// Fix for broken default icons in Leaflet with Webpack/CRA
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

const RoutingMachine = ({ onRouteFound, waypoints, isDrivingMode }) => {
    const { i18n } = useTranslation();
    const map = useMap();
    const routingControlRef = useRef(null);
    const prevLanguageRef = useRef(i18n.language);

    const currentBearing = useSelector(selectCurrentBearing);
    const isHeadingUp = useSelector(selectIsHeadingUp);
    const markersRef = useRef([]);

    // Update markers rotation when bearing changes
    useEffect(() => {
        const rotation = isHeadingUp ? currentBearing : 0;

        // Optimize: Update DOM directly to avoid flickering from setIcon()
        markersRef.current.forEach(marker => {
            if (marker) {
                const element = marker.getElement();
                if (element) {
                    const rotator = element.querySelector('.route-marker-rotator');
                    if (rotator) {
                        rotator.style.transform = `rotate(${rotation}deg)`;
                    } else {
                        // Fallback if element exists but class not found
                        const icon = createRotatedRouteIcon(rotation);
                        marker.setIcon(icon);
                    }
                }
            }
        });
    }, [currentBearing, isHeadingUp]);

    // Initialize routing control
    useEffect(() => {
        if (!map) return;

        const routingControl = L.Routing.control({
            waypoints: [], // Start empty, update later
            routeWhileDragging: true,
            geocoder: null, // Hide default input boxes (L.Control.Geocoder.nominatim(),)
            router: L.Routing.osrmv1({
                serviceUrl: config.osrmServiceUrl,
                language: i18n.language === 'fi-tampere' ? 'fi' : i18n.language.split('-')[0]
            }), lineOptions: {
                styles: [{ color: "#6FA1EC", weight: 4 }]
            },
            show: true,
            addWaypoints: false,
            draggableWaypoints: false,
            fitSelectedRoutes: true,
            showAlternatives: false,
            createMarker: function (i, wp, nWps) {
                // Reset refs array on first marker to ensure clean state
                if (i === 0) markersRef.current = [];

                const marker = L.marker(wp.latLng, {
                    draggable: false,
                    icon: createRotatedRouteIcon(isHeadingUp ? currentBearing : 0)
                });

                // Add to ref for rotation updates
                markersRef.current.push(marker);

                return marker;
            },
            collapsible: true, // Enable collapsible panel
            collapsed: false // Start expanded
        }).addTo(map);

        routingControl.on('routesfound', function (e) {
            const routes = e.routes;
            const route = routes[0];
            if (onRouteFound && route) {
                const plainCoordinates = route.coordinates.map(c => ({ lat: c.lat, lng: c.lng }));
                onRouteFound(plainCoordinates);
            }

            if (route && route.coordinates && route.coordinates.length > 0) {
                const bounds = L.latLngBounds(route.coordinates);
                map.fitBounds(bounds);
            }
        });

        routingControl.on('routeselected', function (e) {
            const route = e.route;
            if (onRouteFound && route) {
                const plainCoordinates = route.coordinates.map(c => ({ lat: c.lat, lng: c.lng }));
                onRouteFound(plainCoordinates);
            }
        });

        routingControlRef.current = routingControl;

        // Hide initially
        const container = routingControl.getContainer();
        if (container) {
            container.style.display = 'none';
        }

        return () => {
            if (map && routingControl) {
                map.removeControl(routingControl);
            }
            routingControlRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, onRouteFound]); // Only run on mount/map change or if callback changes

    // Update waypoints when they change
    useEffect(() => {
        if (routingControlRef.current) {
            // Reset marker refs before setting new waypoints (which will trigger createMarker)
            markersRef.current = [];
            routingControlRef.current.setWaypoints(waypoints || []);

            // Hide container if not enough waypoints
            const container = routingControlRef.current.getContainer();
            if (container) {
                container.style.display = (waypoints && waypoints.length > 1) ? 'block' : 'none';

                // Reset collapsed state when new route is created
                if (waypoints && waypoints.length > 1 && !isDrivingMode) {
                    const isCollapsed = container.classList.contains('leaflet-routing-container-hide');
                    if (isCollapsed) {
                        const closeButton = container.querySelector('.leaflet-routing-collapse-btn');
                        if (closeButton) closeButton.click();
                    }
                }
            }
        }
    }, [waypoints, isDrivingMode]);

    // Update router language when language changes
    useEffect(() => {
        if (routingControlRef.current) {
            const router = routingControlRef.current.getRouter();
            if (router && router.options) {
                const currentLang = i18n.language === 'fi-tampere' ? 'fi' : i18n.language.split('-')[0];
                router.options.language = currentLang;
                // Only force route recalculation if language actually changed
                // (Waypoints update already triggers a route via setWaypoints)
                if (prevLanguageRef.current !== i18n.language && waypoints && waypoints.length > 1) {
                    routingControlRef.current.route();
                }
                prevLanguageRef.current = i18n.language;
            }
        }
    }, [i18n.language, waypoints]);

    // Auto-collapse when entering driving mode
    useEffect(() => {
        if (routingControlRef.current) {
            const container = routingControlRef.current.getContainer();
            if (container && waypoints && waypoints.length > 1) {
                const closeButton = container.querySelector('.leaflet-routing-collapse-btn');
                const isCollapsed = container.classList.contains('leaflet-routing-container-hide');

                if (isDrivingMode && !isCollapsed) {
                    // Collapse when entering driving mode
                    if (closeButton) {
                        closeButton.click();
                    }
                } else if (!isDrivingMode && isCollapsed) {
                    // Expand when exiting driving mode
                    if (closeButton) {
                        closeButton.click();
                    }
                }
            }
        }
    }, [isDrivingMode, waypoints]);

    return null;
};

export default RoutingMachine;
